import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAccount } from "wagmi";
import { useArticle } from "@/hooks/useArticles";
import { useArticleContent } from "@/hooks/useArticleContent";
import { useAuth } from "@/context/useAuth";
import { ArticleHeader } from "@/components/article/ArticleHeader";
import { ArticlePreview } from "@/components/article/ArticlePreview";
import { ArticleContent } from "@/components/article/ArticleContent";
import { PaywallBanner } from "@/components/article/PaywallBanner";
import { PaymentStatus } from "@/components/payment/PaymentStatus";
import { PaymentModal } from "@/components/payment/PaymentModal";
import { CommentsSection } from "@/components/comments/CommentsSection";
import { Skeleton } from "@/components/ui/Skeleton";
import type { PaymentAccept } from "@/lib/x402";

export function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const { data: article, isLoading: metaLoading } = useArticle(id || "");
  const { state, fetchContent, pay } = useArticleContent(id || "");
  const { address, isConnected } = useAccount();
  const { isAuthenticated, getToken, userId } = useAuth();

  const isArticleWriter = isAuthenticated && !!article && article.writerId === userId;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAccept, setSelectedAccept] = useState<PaymentAccept | null>(null);

  useEffect(() => {
    if (id) fetchContent();
  }, [id, fetchContent]);

  const handlePay = useCallback((accept: PaymentAccept) => {
    setSelectedAccept(accept);
    setModalOpen(true);
  }, []);

  const handleWalletPay = useCallback(() => {
    if (!selectedAccept) return;
    setModalOpen(false);
    pay(selectedAccept);
  }, [selectedAccept, pay]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  if (metaLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Skeleton className="h-8 w-2/3 mb-4" />
        <Skeleton className="h-4 w-1/3 mb-8" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-display font-bold mb-2">
          Article not found
        </h2>
        <p className="text-grey font-body">
          This article may have been removed or unpublished.
        </p>
      </div>
    );
  }

  const isProcessing =
    state.step === "switching-chain" ||
    state.step === "signing" ||
    state.step === "submitting";

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <ArticleHeader article={article} />

      {state.step === "paid" && (
        <ArticleContent markdown={state.contentMarkdown} />
      )}

      {state.step === "paywall" && (
        <>
          {article.previewMarkdown && (
            <ArticlePreview markdown={article.previewMarkdown} />
          )}
          <PaywallBanner
            accepts={state.accepts}
            onPay={handlePay}
            loading={false}
          />
        </>
      )}

      {isProcessing && (
        <>
          {article.previewMarkdown && (
            <ArticlePreview markdown={article.previewMarkdown} />
          )}
          <PaymentStatus state={state} />
        </>
      )}

      {state.step === "error" && (
        <>
          {article.previewMarkdown && (
            <ArticlePreview markdown={article.previewMarkdown} />
          )}
          <PaymentStatus state={state} />
          {state.step === "error" && (
            <PaywallBanner accepts={[]} onPay={handlePay} />
          )}
        </>
      )}

      {state.step === "loading" && <Skeleton className="h-64 mt-4" />}
      {state.step === "idle" && <Skeleton className="h-64 mt-4" />}

      {id && (
        <CommentsSection
          articleId={id}
          walletAddress={address}
          canComment={state.step === "paid" || isArticleWriter}
          isConnected={isConnected}
          jwt={isArticleWriter ? getToken() : undefined}
          isArticleWriter={isArticleWriter}
        />
      )}

      <PaymentModal
        open={modalOpen}
        onClose={handleModalClose}
        accept={selectedAccept}
        onWalletPay={handleWalletPay}
      />
    </div>
  );
}
